<template>
  <div class="compact-login-panel">
    <div class="login-tabs">
      <div
        class="tab-item"
        :class="{ active: activeTab === 'account' }"
        @click="activeTab = 'account'"
      >账号登录</div>
      <div
        class="tab-item"
        :class="{ active: activeTab === 'phone' }"
        @click="activeTab = 'phone'"
      >手机登录</div>
    </div>

    <el-form
      v-if="activeTab === 'account'"
      ref="loginForm"
      :model="loginForm"
      :rules="loginRules"
      class="login-form"
    >
      <el-form-item prop="username">
        <div class="input-wrapper">
          <img src="@/assets/image/zh1.png" class="input-icon" alt="">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入你的账号"
            name="username"
            type="text"
            tabindex="1"
            auto-complete="on"
          />
        </div>
      </el-form-item>
      <el-form-item prop="password">
        <div class="input-wrapper">
          <img src="@/assets/image/psw1.png" class="input-icon" alt="">
          <el-input
            :key="passwordType"
            ref="password"
            v-model="loginForm.password"
            :type="passwordType"
            placeholder="请输入密码"
            name="password"
            tabindex="2"
            auto-complete="on"
            @keyup.enter.native="handleLogin"
          />
          <span class="show-pwd" @click="showPwd">
            <svg-icon :icon-class="passwordType === 'password' ? 'eye' : 'eye-open'" />
          </span>
        </div>
      </el-form-item>
      <el-form-item prop="captcha">
        <div class="input-wrapper captcha-row">
          <el-input
            v-model="loginForm.captcha"
            placeholder="请输入图形验证码"
            name="captcha"
            tabindex="3"
            auto-complete="off"
            class="captcha-input"
            @keyup.enter.native="handleLogin"
          />
          <img
            class="captcha-img"
            :src="captchaUrl"
            alt="图形验证码"
            @click="refreshCaptcha"
          >
        </div>
      </el-form-item>
      <div class="form-links">
        <span />
        <span class="link-item" @click="goToForget">忘记密码</span>
      </div>
      <register-agreement ref="accountAgreement" />
      <div v-if="embedded && formError" class="inline-form-error">{{ formError }}</div>
      <el-button
        :loading="loading"
        type="primary"
        class="login-btn"
        @click.native.prevent="handleLogin"
      >登录</el-button>
    </el-form>

    <el-form
      v-if="activeTab === 'phone'"
      ref="phoneForm"
      :model="phoneForm"
      :rules="phoneRules"
      class="login-form"
    >
      <el-form-item prop="phone">
        <div class="input-wrapper">
          <img src="@/assets/image/sjh1.png" class="input-icon" alt="">
          <el-input
            v-model="phoneForm.phone"
            placeholder="请输入手机号"
            name="phone"
            type="text"
            tabindex="1"
            auto-complete="on"
          />
        </div>
      </el-form-item>
      <el-form-item prop="code">
        <div class="input-wrapper code-row">
          <img src="@/assets/image/code1.png" class="input-icon" alt="">
          <el-input
            v-model="phoneForm.code"
            placeholder="请输入验证码"
            name="code"
            type="text"
            tabindex="2"
            auto-complete="off"
            class="code-input"
            @keyup.enter.native="handlePhoneLogin"
          />
          <el-button
            type="text"
            size="small"
            class="code-btn"
            :disabled="codeCountdown > 0"
            @click="sendCode"
          >{{ codeCountdown > 0 ? `${codeCountdown}s` : '获取验证码' }}</el-button>
        </div>
      </el-form-item>
      <div class="form-links">
        <span />
        <span class="link-item" @click="goToForget">忘记密码</span>
      </div>
      <register-agreement ref="phoneAgreement" />
      <div v-if="embedded && formError" class="inline-form-error">{{ formError }}</div>
      <el-button
        :loading="loading"
        type="primary"
        class="login-btn"
        @click.native.prevent="handlePhoneLogin"
      >登录</el-button>
    </el-form>
  </div>
</template>

<script>
import { accountLogin, sendLoginSmsCode, mobileLogin } from '@/api/user'
import { setToken, setUserInfo } from '@/utils/auth'
import RegisterAgreement from '@/components/RegisterAgreement.vue'

export default {
  name: 'CompactLoginPanel',
  components: { RegisterAgreement },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const validateUsername = (rule, value, callback) => {
      if (!value || value.trim() === '') callback(new Error('请输入用户名'))
      else callback()
    }
    const validatePassword = (rule, value, callback) => {
      if (!value || value.length < 6) callback(new Error('密码不能少于6位'))
      else callback()
    }
    const validateCaptcha = (rule, value, callback) => {
      if (!value || value.trim() === '') callback(new Error('请输入图形验证码'))
      else callback()
    }
    const validatePhone = (rule, value, callback) => {
      if (!/^1[3-9]\d{9}$/.test(value)) callback(new Error('请输入正确的手机号'))
      else callback()
    }
    const validateCode = (rule, value, callback) => {
      if (!/^\d{6}$/.test(value)) callback(new Error('请输入6位验证码'))
      else callback()
    }
    return {
      activeTab: 'account',
      loginForm: { username: '', password: '', captcha: '' },
      phoneForm: { phone: '', code: '' },
      loginRules: {
        username: [{ required: true, trigger: 'blur', validator: validateUsername }],
        password: [{ required: true, trigger: 'blur', validator: validatePassword }],
        captcha: [{ required: true, trigger: 'blur', validator: validateCaptcha }]
      },
      phoneRules: {
        phone: [{ required: true, trigger: 'blur', validator: validatePhone }],
        code: [{ required: true, trigger: 'blur', validator: validateCode }]
      },
      loading: false,
      passwordType: 'password',
      codeCountdown: 0,
      captchaUrl: '',
      captchaObjectUrl: '', // deprecated: kept for backward compatibility
      formError: ''
    }
  },
  watch: {
    activeTab() {
      this.formError = ''
    },
    embedded(val) {
      if (val) this.formError = ''
    }
  },
  mounted() {
    this.refreshCaptcha()
  },
  methods: {
    refreshCaptcha() {
      const t = Date.now()
      this.captchaUrl = `/api/user/captcha?t=${t}`
    },
    showPwd() {
      this.passwordType = this.passwordType === 'password' ? '' : 'password'
      this.$nextTick(() => this.$refs.password && this.$refs.password.focus())
    },
    async afterLoginSuccess(response) {
      const aaa = response.data
      setToken(aaa.userinfo.token)
      setUserInfo(aaa.userinfo)
      // 同步到 vuex，保证顶部导航栏等依赖 token 的响应式状态会立刻更新
      try {
        await this.$store.dispatch('user/setToken', aaa.userinfo.token)
      } catch (e) {
        // ignore：即使同步失败，后续 getInfo 也会更新用户信息
      }
      try {
        await this.$store.dispatch('user/getInfo')
      } catch (e) {
        console.error(e)
      }
      this.$message.success('登录成功')
      this.$emit('success')
    },
    checkAgreement(refName) {
      const ref = this.$refs[refName]
      return !ref || ref.validate()
    },
    handleLogin() {
      if (!this.checkAgreement('accountAgreement')) return
      this.$refs.loginForm.validate(valid => {
        if (!valid) return
        if (this.embedded) this.formError = ''
        this.loading = true
        const silentOpt = this.embedded ? { silentError: true } : {}
        accountLogin({
          account: this.loginForm.username,
          password: this.loginForm.password,
          captcha: this.loginForm.captcha
        }, silentOpt).then(response => {
          this.loading = false
          if (response.code === 1) this.afterLoginSuccess(response)
        }).catch(err => {
          this.loading = false
          if (this.embedded) {
            this.formError = (err && err.message) || '登录失败，请重试'
          }
          this.refreshCaptcha()
        })
      })
    },
    handlePhoneLogin() {
      if (!this.checkAgreement('phoneAgreement')) return
      this.$refs.phoneForm.validate(valid => {
        if (!valid) return
        if (this.embedded) this.formError = ''
        this.loading = true
        const silentOpt = this.embedded ? { silentError: true } : {}
        mobileLogin({
          mobile: this.phoneForm.phone,
          captcha: this.phoneForm.code
        }, silentOpt).then(response => {
          this.loading = false
          if (response.code === 1) this.afterLoginSuccess(response)
        }).catch(err => {
          this.loading = false
          if (this.embedded) {
            this.formError = (err && err.message) || '登录失败，请重试'
          }
        })
      })
    },
    sendCode() {
      if (this.codeCountdown > 0) return
      this.$refs.phoneForm.validateField('phone', (errorMessage) => {
        if (errorMessage) {
          if (this.embedded) this.formError = errorMessage
          else this.$message.error(errorMessage)
          return
        }
        if (this.embedded) this.formError = ''
        const silentOpt = this.embedded ? { silentError: true } : {}
        sendLoginSmsCode({
          mobile: this.phoneForm.phone,
          event: 'login'
        }, silentOpt).then(() => {
          this.codeCountdown = 60
          const timer = setInterval(() => {
            this.codeCountdown--
            if (this.codeCountdown <= 0) clearInterval(timer)
          }, 1000)
          this.$message.success('验证码已发送')
        }).catch(err => {
          if (this.embedded) {
            this.formError = (err && err.message) || '验证码发送失败'
          }
        })
      })
    },
    goToForget() {
      this.$emit('forgot')
    }
  }
}
</script>

<style lang="scss" scoped>
.compact-login-panel {
  padding-top: 4px;
}

.login-tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;

  .tab-item {
    flex: 1;
    text-align: center;
    padding: 12px 0;
    font-size: 15px;
    color: #999;
    cursor: pointer;
    position: relative;

    &.active {
      color: #111;
      font-weight: 600;
      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 50%;
        transform: translateX(-50%);
        width: 50%;
        height: 2px;
        background: #0f6fe2;
        border-radius: 1px;
      }
    }
  }
}

.login-form {
  .input-wrapper {
    display: flex;
    align-items: center;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 0 12px;
    background: #fff;

    &:focus-within {
      border-color: #4a90e2;
    }

    .input-icon {
      width: 18px;
      height: 18px;
      margin-right: 8px;
      flex-shrink: 0;
    }

    ::v-deep .el-input__inner {
      border: none;
      padding: 12px 0;
    }

    .show-pwd {
      cursor: pointer;
      color: #999;
      margin-left: 8px;
    }

    &.code-row .code-btn {
      color: #0f6fe2;
      font-weight: 600;
      flex-shrink: 0;
    }
  }

  .form-links {
    display: flex;
    justify-content: space-between;
    margin: 14px 0;

    .link-item {
      color: #0f6fe2;
      cursor: pointer;
      font-size: 13px;
    }
  }

  .inline-form-error {
    margin-bottom: 8px;
    font-size: 13px;
    line-height: 1.45;
    color: #f56c6c;
    text-align: left;
  }

  .login-btn {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    background: linear-gradient(90deg, #0f6fe2, #0c89f2);
    border: none;
    margin-top: 8px;
  }

  .captcha-row {
    gap: 12px;
  }

  .captcha-input {
    flex: 1;
  }

  .captcha-img {
    width: 96px;
    height: 34px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    cursor: pointer;
    object-fit: cover;
    flex-shrink: 0;
  }
}
</style>
